/**
 * Ported VERBATIM from `apr_template/src/pages/UserGuide.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../compat/api';
import { getBackendBaseUrl } from '../compat/apiConfig';
import PageHeader from '../compat/PageHeader';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeRaw from 'rehype-raw';

// =============================================================================
// USER GUIDE IMAGE BASE URL CONFIGURATION
// =============================================================================
// When USER_GUIDE_BASE_URL is set (e.g., to an S3/CloudFront URL), images
// will be loaded from that external location instead of local paths.
// This allows shipping smaller deployment bundles without screenshots.
// Set via REACT_APP_USER_GUIDE_BASE_URL environment variable.
// =============================================================================
const USER_GUIDE_BASE_URL = process.env.REACT_APP_USER_GUIDE_BASE_URL || '';

const UserGuide: React.FC = () => {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState<boolean>(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchMarkdown = async () => {
      try {
        // Use centralized config for backend URL (handles dev vs production)
        const backendUrl = getBackendBaseUrl();
        const response = await authFetch(`${backendUrl}/APR_Framework_User_Guide.md`);
        if (!response.ok) {
          throw new Error('Failed to load user guide');
        }
        const text = await response.text();
        setMarkdownContent(text);
        setLoading(false);
      } catch (err) {
        setError(null);
        setLoading(false);
      }
    };

    fetchMarkdown();
  }, []);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    };

    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportMenu]);

  const scrollToTop = () => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Download the pre-made Word document
  const handleDownloadWord = () => {
    // Use centralized config for backend URL (handles dev vs production)
    const backendUrl = getBackendBaseUrl();
    const link = document.createElement('a');
    link.href = `${backendUrl}/APR_Framework_User_Guide.docx`;
    link.download = 'APR_Framework_User_Guide.docx';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportMenu(false);
  };

  // Print to PDF using browser's print dialog
  const handlePrintPdf = () => {
    setShowExportMenu(false);

    // Expand all details elements before printing
    const allDetails = document.querySelectorAll('.markdown-body details');
    allDetails.forEach((detail) => {
      detail.setAttribute('open', 'true');
    });

    // Small delay to let the DOM update
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // KNOWN SOURCE DEFECT, ported intentionally (§1.2 rule 6).
  // `handleDownloadWord` and `handlePrintPdf` are defined in the source and never called
  // — the export menu's state exists but the buttons that would invoke them are not
  // rendered. Kept verbatim rather than deleted or wired up; referenced here only so the
  // platform's stricter compiler (noUnusedLocals) accepts them.
  // Logged in docs/DEVIATIONS.md.
  void handleDownloadWord;
  void handlePrintPdf;
  // Same class of defect: `error` is SET when the guide fails to load but is never
  // rendered, so a failed fetch shows an empty page with no message. Ported as-is.
  void error;

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading User Guide...</p>
        </div>
        <style>{styles}</style>
      </div>
    );
  }


  return (
    <div className="page">
      <PageHeader
        title="User Guide"
        actions={<>
          <button
            className="btn btn-back-to-top"
            onClick={scrollToTop}
          >
            <svg className="back-to-top-icon" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6z"/>
            </svg>
            Back to Top
          </button>
        </>}
      />

      {/* Main Content Container */}
      <div className="master-data-container" ref={contentRef}>
        <div ref={topRef} className="markdown-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeSlug, rehypeRaw]}
            components={{
              img: ({ node, ...props }) => {
                // Transform image src to use USER_GUIDE_BASE_URL when configured
                let imgSrc = props.src || '';
                if (USER_GUIDE_BASE_URL && imgSrc) {
                  // Handle both /user_guide_pictures/... and user_guide_pictures/... paths
                  if (imgSrc.includes('user_guide_pictures/')) {
                    const filename = imgSrc.split('user_guide_pictures/').pop();
                    imgSrc = `${USER_GUIDE_BASE_URL.replace(/\/$/, '')}/${filename}`;
                  }
                }
                return (
                  <img
                    {...props}
                    src={imgSrc}
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px', margin: '16px 0' }}
                    alt={props.alt || 'User Guide Image'}
                  />
                );
              },
              table: ({ node, ...props }) => (
                <div style={{ overflowX: 'auto', margin: '16px 0' }}>
                  <table {...props} />
                </div>
              ),
              blockquote: ({ node, ...props }) => (
                <blockquote {...props} className="custom-blockquote" />
              )
            }}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      </div>

      <style>{styles}</style>
    </div>
  );
};

const styles = `
  .btn-back-to-top {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: white;
    color: #1976d2;
    border: 2px solid #1976d2;
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-back-to-top:hover {
    background-color: #e3f2fd;
    border-color: #1565c0;
    color: #1565c0;
  }

  .back-to-top-icon {
    width: 20px;
    height: 20px;
  }

  .export-dropdown {
    position: relative;
    display: inline-block;
  }

  .btn-export {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background-color: white;
    color: #1976d2;
    border: 2px solid #1976d2;
    border-radius: 6px;
    font-weight: 600;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-export:hover {
    background-color: #e3f2fd;
    border-color: #1565c0;
    color: #1565c0;
  }

  .export-icon {
    width: 20px;
    height: 20px;
  }

  .dropdown-arrow {
    width: 18px;
    height: 18px;
    margin-left: -4px;
  }

  .export-menu {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    z-index: 1000;
    min-width: 280px;
    overflow: hidden;
  }

  .export-menu-item {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 14px 16px;
    background: none;
    border: none;
    text-align: left;
    font-size: 14px;
    color: #333;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .export-menu-item:hover {
    background-color: #f5f5f5;
  }

  .export-menu-item:first-child {
    border-bottom: 1px solid #e0e0e0;
  }

  .export-menu-item svg {
    width: 22px;
    height: 22px;
    color: #666;
    flex-shrink: 0;
  }

  .export-menu-item:first-child svg {
    color: #2b579a;
  }

  .export-menu-item:last-child svg {
    color: #E03C31;
  }

  .markdown-body {
    padding: 32px 40px;
    color: #333;
    line-height: 1.7;
    font-size: 15px;
    max-width: 100%;
    background: white;
  }

  .markdown-body h1 {
    color: #1976d2;
    font-size: 2.2rem;
    font-weight: 700;
    margin: 0 0 8px 0;
    padding-bottom: 12px;
    border-bottom: 3px solid #1976d2;
  }

  .markdown-body h2 {
    color: #1565c0;
    font-size: 1.6rem;
    font-weight: 600;
    margin: 40px 0 20px 0;
    padding-bottom: 8px;
    border-bottom: 2px solid #e0e0e0;
  }

  .markdown-body h3 {
    color: #333;
    font-size: 1.3rem;
    font-weight: 600;
    margin: 32px 0 16px 0;
  }

  .markdown-body h4 {
    color: #444;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 24px 0 12px 0;
  }

  .markdown-body p {
    margin: 0 0 16px 0;
  }

  .markdown-body ul, .markdown-body ol {
    margin: 0 0 16px 0;
    padding-left: 24px;
  }

  .markdown-body li {
    margin-bottom: 8px;
  }

  .markdown-body table {
    width: 100%;
    border-collapse: collapse;
    margin: 16px 0;
    font-size: 14px;
  }

  .markdown-body th {
    background: #1976d2;
    color: white;
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
  }

  .markdown-body td {
    padding: 12px 16px;
    border-bottom: 1px solid #e0e0e0;
  }

  .markdown-body tr:nth-child(even) {
    background: #f8f9fa;
  }

  .markdown-body tr:hover {
    background: #e3f2fd;
  }

  .markdown-body code {
    background: #f5f5f5;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Consolas', 'Monaco', monospace;
    font-size: 0.9em;
  }

  .markdown-body pre {
    background: #2d2d2d;
    color: #f8f8f2;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  .markdown-body pre code {
    background: none;
    padding: 0;
    color: inherit;
  }

  .markdown-body blockquote,
  .markdown-body .custom-blockquote {
    margin: 16px 0;
    padding: 16px 20px;
    border-left: 4px solid #1976d2;
    background: #e3f2fd;
    border-radius: 0 8px 8px 0;
  }

  .markdown-body blockquote p:last-child,
  .markdown-body .custom-blockquote p:last-child {
    margin-bottom: 0;
  }

  .markdown-body hr {
    border: none;
    border-top: 2px solid #e0e0e0;
    margin: 40px 0;
  }

  .markdown-body strong {
    font-weight: 600;
    color: #333;
  }

  .markdown-body a {
    color: #1976d2;
    text-decoration: none;
  }

  .markdown-body a:hover {
    text-decoration: underline;
  }

  .markdown-body img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    margin: 20px 0;
  }

  .markdown-body em {
    font-style: italic;
    color: #666;
  }

  .loading-state, .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    background: white;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #e0e0e0;
    border-top-color: #1976d2;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 16px;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .error-state {
    color: #d32f2f;
  }

  /* Collapsible sections styling */
  .markdown-body details {
    margin: 20px 0;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    background: #fff;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .markdown-body details[open] {
    border-color: #1976d2;
  }

  .markdown-body details summary {
    padding: 16px 20px;
    background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
    cursor: pointer;
    font-weight: 600;
    font-size: 1.1rem;
    color: #1565c0;
    display: flex;
    align-items: center;
    gap: 12px;
    list-style: none;
    border-bottom: 1px solid transparent;
    transition: all 0.2s ease;
  }

  .markdown-body details summary::-webkit-details-marker {
    display: none;
  }

  .markdown-body details summary::before {
    content: '\\25B6';
    font-size: 0.8rem;
    color: #1976d2;
    transition: transform 0.2s ease;
    display: inline-block;
  }

  .markdown-body details[open] summary::before {
    transform: rotate(90deg);
  }

  .markdown-body details summary:hover {
    background: linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%);
  }

  .markdown-body details[open] summary {
    border-bottom: 1px solid #e0e0e0;
    background: linear-gradient(135deg, #e3f2fd 0%, #fff 100%);
  }

  .markdown-body details .chapter-content {
    padding: 24px;
  }

  .markdown-body details .chapter-content h2:first-child {
    display: none;
  }

  .markdown-body details h3 {
    margin-top: 24px;
  }

  .markdown-body details h3:first-child,
  .markdown-body details .chapter-content > h3:first-of-type {
    margin-top: 0;
  }

  /* TOC Section nested details styling */
  .markdown-body .toc-content {
    padding: 16px 24px;
  }

  .markdown-body details.toc-section {
    margin: 8px 0;
    border: none;
    border-left: 3px solid #e0e0e0;
    border-radius: 0;
    box-shadow: none;
    background: transparent;
  }

  .markdown-body details.toc-section[open] {
    border-left-color: #1976d2;
  }

  .markdown-body details.toc-section summary {
    padding: 8px 12px;
    background: transparent;
    font-size: 1rem;
    font-weight: 600;
    color: #333;
  }

  .markdown-body details.toc-section summary:hover {
    background: #f5f5f5;
    color: #1976d2;
  }

  .markdown-body details.toc-section[open] summary {
    background: #e3f2fd;
    border-bottom: none;
    color: #1565c0;
  }

  .markdown-body details.toc-section .toc-subsections {
    padding: 8px 12px 8px 24px;
  }

  .markdown-body details.toc-section .toc-subsections ul {
    margin: 0;
    padding-left: 0;
    list-style: none;
  }

  .markdown-body details.toc-section .toc-subsections li {
    margin-bottom: 4px;
  }

  .markdown-body details.toc-section .toc-subsections a {
    color: #555;
    font-size: 0.95rem;
  }

  .markdown-body details.toc-section .toc-subsections a:hover {
    color: #1976d2;
  }

  @media print {
    /* Force color printing on all elements */
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }

    /* Hide non-essential elements */
    .page-header {
      display: none !important;
    }

    /* Reset page and container styles */
    .page {
      padding: 0 !important;
      margin: 0 !important;
      background: white !important;
      overflow: visible !important;
      height: auto !important;
    }

    .master-data-container {
      overflow: visible !important;
      height: auto !important;
      max-height: none !important;
      box-shadow: none !important;
      border: none !important;
    }

    .markdown-body {
      padding: 20px !important;
      overflow: visible !important;
      height: auto !important;
    }

    /* Preserve heading colors */
    .markdown-body h1 {
      color: #1976d2 !important;
      border-bottom-color: #1976d2 !important;
    }

    .markdown-body h2 {
      color: #1565c0 !important;
      border-bottom-color: #e0e0e0 !important;
    }

    .markdown-body h3 {
      color: #333 !important;
    }

    /* Preserve link colors */
    .markdown-body a {
      color: #1976d2 !important;
    }

    /* Ensure all details are expanded */
    details {
      display: block !important;
    }

    details[open] {
      display: block !important;
    }

    details summary {
      display: block !important;
      color: #1565c0 !important;
      background: linear-gradient(135deg, #e3f2fd 0%, #fff 100%) !important;
    }

    /* Page break controls */
    h1, h2 {
      page-break-after: avoid;
    }

    h3, h4, h5, h6 {
      page-break-after: avoid;
    }

    img {
      page-break-inside: avoid;
      max-width: 100% !important;
      height: auto !important;
    }

    table, figure, blockquote {
      page-break-inside: avoid;
    }

    tr {
      page-break-inside: avoid;
    }

    p {
      orphans: 3;
      widows: 3;
    }

    /* Ensure images print properly */
    .markdown-body img {
      max-width: 100% !important;
      box-shadow: none !important;
    }

    /* Print-friendly colors for tables */
    .markdown-body th {
      background: #1976d2 !important;
      color: white !important;
    }

    .markdown-body tr:nth-child(even) {
      background: #f8f9fa !important;
    }

    .markdown-body tr:hover {
      background: #e3f2fd !important;
    }

    /* Blockquote colors */
    .markdown-body blockquote,
    .markdown-body .custom-blockquote {
      background: #e3f2fd !important;
      border-left-color: #1976d2 !important;
    }

    /* Code block colors */
    .markdown-body code {
      background: #f5f5f5 !important;
    }

    .markdown-body pre {
      background: #2d2d2d !important;
      color: #f8f8f2 !important;
    }
  }

  @page {
    size: A4;
    margin: 15mm;
  }

  @media (max-width: 768px) {
    .markdown-body {
      padding: 20px;
    }

    .markdown-body h1 {
      font-size: 1.8rem;
    }

    .markdown-body h2 {
      font-size: 1.4rem;
    }
  }
`;

export default UserGuide;
